import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Tag, Check, SkipForward, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CollapseBody, CollapseHeader } from './collapse-toggle';
import { describePendingAction as describe } from './sentinel-workspace.helpers';
import type { PendingActionResultDto, PendingActionsDto } from './types';

/**
 * The post-processing actions waiting on a ticket a person is handling. They belong to the ticket,
 * not the draft, so they stay runnable after an edited reply has gone out.
 */

interface PendingActionsCardProps {
  actions: PendingActionsDto;
  /** Absent for a reader — the card then shows what is waiting, without offering to run it. */
  onRun?: () => Promise<void>;
  /** Where there is no ticket to run them on: the actions are only listed, under this title and note. */
  preview?: { title: string; note: string };
}

const runLabel = (count: number) => (count === 1 ? 'Run 1 action' : `Run ${count} actions`);

const STATUS_ICON = {
  executed: <Check className="size-3.5 text-green-600" />,
  skipped: <SkipForward className="size-3.5 text-gray-800" />,
  failed: <TriangleAlert className="size-3.5 text-red-500" />,
};

export function PendingActionsCard({ actions, onRun, preview }: PendingActionsCardProps) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pending = actions.status === 'pending';
  // A partial or failed run can be run again: the backend skips what is already done.
  const runnable = pending || actions.status === 'partial' || actions.status === 'failed';
  // Paired by position (one result per action, in plan order), since two actions can share a tool.
  // A length mismatch shows no results rather than mispairing them.
  const results: PendingActionResultDto[] | null =
    actions.results?.length === actions.plan.actions.length ? actions.results : null;

  const run = async () => {
    if (!onRun) return;
    setBusy(true);
    setError(null);
    try {
      await onRun();
    } catch (err) {
      // The server's reason ("already been run", "turned off"), never axios's "status code 409".
      const message = isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : err instanceof Error
          ? err.message
          : undefined;
      setError(message || 'The actions could not be run. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex w-full flex-col border-l-4 border-solid border-amber-500 bg-gray-100">
      <div className="flex w-full flex-col bg-white px-5 pb-4 pt-4">
        <CollapseHeader open={open} label="Actions for this ticket" variant="chevron" onToggle={() => setOpen((v) => !v)} className="h-5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-5 shrink-0 items-center justify-center rounded bg-amber-500 text-white">
              <Tag className="size-3.5" />
            </span>
            <p className="truncate text-[14px] font-semibold leading-[1.1] text-text-body">
              {preview?.title ??
                (pending ? 'Actions for this ticket' : actions.status === 'running' ? 'Running actions' : 'Actions run')}
            </p>
          </div>
        </CollapseHeader>
      </div>

      <CollapseBody open={open}>
        <div className="flex w-full flex-col gap-3 px-4 pt-3 pb-3">
          <div className="flex w-full flex-col rounded-xl border border-gray-100 bg-white drop-shadow-[0_0_3px_rgba(0,0,0,0.06)]">
            {actions.plan.actions.map((action, position) => {
              const result = results?.[position];
              const { label, value } = describe(action);
              return (
                <div key={`${action.tool}-${position}`} className="flex w-full flex-col">
                  {position > 0 && <div className="h-px w-full bg-gray-500" />}
                  <div className="flex w-full items-start gap-2 px-4 py-2.5">
                    <span className="mt-0.5 shrink-0">
                      {result ? STATUS_ICON[result.status] : <Tag className="size-3.5 text-amber-500" />}
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <p className="text-[13px] leading-[18px] text-text-body">
                        {label}
                        {value && <span className="text-gray-800"> · {value}</span>}
                      </p>
                      {result?.detail && (
                        <p className="text-[12px] leading-[16px] text-gray-800">{result.detail}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {preview ? (
            <p className="text-[12px] leading-[16px] text-gray-800">{preview.note}</p>
          ) : runnable ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-[12px] leading-[16px] text-gray-800">
                {pending
                  ? 'Send your reply first, then run these on the ticket. On an automatic reply, Kim runs them itself.'
                  : 'Some actions did not run. Running again skips what is already done.'}
              </p>
              {onRun && (
                <Button size="sm" variant="outline" disabled={busy} onClick={run} className="self-start">
                  {busy ? 'Running…' : pending ? runLabel(actions.plan.actions.length) : 'Run again'}
                </Button>
              )}
              {error && (
                <p role="alert" className="text-[12px] leading-[16px] text-red-500">
                  {error}
                </p>
              )}
            </div>
          ) : actions.status === 'running' ? (
            <p className="text-[12px] leading-[16px] text-gray-800">These actions are being run.</p>
          ) : (
            <p className="text-[12px] leading-[16px] text-gray-800">
              {actions.trigger === 'automation' ? 'Run automatically with the reply.' : 'Run from this ticket.'}
            </p>
          )}
        </div>
      </CollapseBody>
    </div>
  );
}
